import React, { useEffect, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, FeatureGroup, Polygon as LeafletPolygon, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw"; // Adds the Draw plugin
import "leaflet.gridlayer.googlemutant"; // Google tiles in Leaflet
import { getToken } from "../../services/tokenGET";
import { saveMockPins, deleteMockPins } from "../../data/mockPins";
import { loadGoogleMapsApi } from "../../utils/loadGoogleMaps";

// Check if running locally
const IS_LOCAL_DEV = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Component to integrate Google Maps tiles in Leaflet
const GoogleMutantLayer = () => {
  const map = useMap();
  const [apiLoaded, setApiLoaded] = useState(false);

  // Load Google Maps API on mount
  useEffect(() => {
    loadGoogleMapsApi()
      .then(() => setApiLoaded(true))
      .catch(err => console.error('Failed to load Google Maps:', err));
  }, []);

  useEffect(() => {
    if (!map || !apiLoaded) return;

    // Add Google layer once API is loaded
    const googleLayer = L.gridLayer.googleMutant({
      type: "roadmap", // can change to either roadmap, satellite, terrain, hybrid
    });
    googleLayer.addTo(map);

    return () => {
      if (googleLayer) {
        map.removeLayer(googleLayer);
      }
    };
  }, [map, apiLoaded]);

  return null;
};

// Component to set up drawing controls inside the map
const DrawControl = ({ featureGroupRef, onPolygonChange }) => {
  const map = useMap();
  const drawControlRef = useRef(null);

  useEffect(() => {
    if (!map || !featureGroupRef.current) return;

    const featureGroup = featureGroupRef.current;

    // Create draw control
    const drawControl = new L.Control.Draw({
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
        },
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: {
        featureGroup: featureGroup,
        edit: true,
        remove: true,
      },
    });

    map.addControl(drawControl);
    drawControlRef.current = drawControl;

    // Handle polygon created
    const handleCreated = (e) => {
      const layer = e.layer;
      featureGroup.clearLayers(); // Only one polygon at a time
      featureGroup.addLayer(layer);
      onPolygonChange(layer);
    };

    // Handle polygon edited
    const handleEdited = (e) => {
      const layers = e.layers;
      layers.eachLayer((layer) => {
        onPolygonChange(layer);
      });
    };

    // Handle polygon deleted
    const handleDeleted = () => {
      onPolygonChange(null);
    };

    map.on(L.Draw.Event.CREATED, handleCreated);
    map.on(L.Draw.Event.EDITED, handleEdited);
    map.on(L.Draw.Event.DELETED, handleDeleted);

    return () => {
      map.off(L.Draw.Event.CREATED, handleCreated);
      map.off(L.Draw.Event.EDITED, handleEdited);
      map.off(L.Draw.Event.DELETED, handleDeleted);
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current);
      }
    };
  }, [map, featureGroupRef, onPolygonChange]);

  return null;
};

// Component to "fly"/zoom to an area when selected
const FlyToArea = ({ selectedArea, allAreas }) => {
  const map = useMap();

  useEffect(() => {
    if (!selectedArea || !allAreas || allAreas.length === 0) return;

    const area = allAreas.find(a => a.name === selectedArea);
    if (!area || !area.pins || area.pins.length < 3) return;

    // Get all positions for this area
    const positions = area.pins
      .sort((a, b) => a.pin_order - b.pin_order)
      .map(pin => [pin.latitude, pin.longitude]);

    // Create bounds from the polygon positions
    const bounds = L.latLngBounds(positions);

    // Fly to the bounds with some padding
    map.flyToBounds(bounds, {
      padding: [50, 50],
      maxZoom: 13, // Zoom how much you want it to zoom in 
      duration: 1.5, // Animation duration in seconds
    });
  }, [selectedArea, allAreas, map]);

  return null;
};

// Component to render a clickable polygon for an area
const AreaPolygon = ({ area, isSelected, onAreaClick }) => {
  const positions = area.pins
    .sort((a, b) => a.pin_order - b.pin_order)
    .map(pin => [pin.latitude, pin.longitude]);

  if (positions.length < 3) return null;

  return (
    <LeafletPolygon
      positions={positions}
      pathOptions={{
        color: isSelected ? '#2563eb' : '#6b7280',
        fillColor: isSelected ? '#3b82f6' : '#9ca3af',
        fillOpacity: isSelected ? 0.4 : 0.2,
        weight: isSelected ? 3 : 2,
      }}
      eventHandlers={{
        click: () => onAreaClick(area.name),
      }}
    />
  );
};

const AdminMap = ({ 
  polygon = [], 
  selectedArea, 
  allAreas = [], 
  onAreaSelect,
  onPolygonSaved 
}) => {
  const featureGroupRef = useRef(null);
  const [polygonLayer, setPolygonLayer] = useState(null);

  // Find the selected area's ID
  const selectedAreaData = allAreas.find(a => a.name === selectedArea);
  const selectedAreaId = selectedAreaData?.id;

  // Callback for polygon changes
  const handlePolygonChange = useCallback((layer) => {
    setPolygonLayer(layer);
  }, []);

  // Load existing polygon data for the SELECTED area when polygon prop changes
  useEffect(() => {
    if (featureGroupRef.current) {
      const fg = featureGroupRef.current;
      fg.clearLayers();
      
      if (polygon && polygon.length > 0) {
        // Convert polygon data to Leaflet format and add to feature group
        const positions = polygon.map(p => [p.lat, p.lng]);
        const leafletPolygon = L.polygon(positions, {
          color: '#2563eb',
          fillColor: '#3b82f6',
          fillOpacity: 0.4,
          weight: 3,
        });
        fg.addLayer(leafletPolygon);
        setPolygonLayer(leafletPolygon);
      } else {
        setPolygonLayer(null);
      }
    }
  }, [polygon, selectedArea]);

  // Handle clicking on an existing polygon
  const handleAreaPolygonClick = useCallback((areaName) => {
    if (onAreaSelect) {
      onAreaSelect(areaName);
    }
  }, [onAreaSelect]);

  // Global functions for editing, clearing, removing, and saving polygons
  useEffect(() => {
    // Enable drawing mode
    window.adminMapEdit = () => {
      if (polygonLayer && polygonLayer.editing) {
        polygonLayer.editing.enable();
      }
    };

    // Clear polygon (local only, doesn't delete from DB)
    window.adminMapClear = () => {
      if (featureGroupRef.current) {
        featureGroupRef.current.clearLayers();
        setPolygonLayer(null);
      }
    };

    // Remove polygon + call backend (or mock for local dev)
    window.adminMapRemove = async () => {
      if (!selectedAreaId) return alert("Vänligen välj ett område först!");
      if (featureGroupRef.current) featureGroupRef.current.clearLayers();
      setPolygonLayer(null);

      // Use mock operations for local development
      if (IS_LOCAL_DEV) {
        deleteMockPins(selectedAreaId);
        alert("Koordinater raderade! (lokal mock)");
        if (onPolygonSaved) onPolygonSaved();
        return;
      }

      try {
        const token = await getToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pins/${selectedAreaId}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": token },
          credentials: "include",
        });

        if (response.ok) {
          alert("Koordinater raderade!");
          if (onPolygonSaved) onPolygonSaved(); // Refresh data
        } else {
          alert("Det gick inte att radera koordinaterna.");
          console.error(response.statusText);
        }
      } catch (err) {
        console.error(err);
        alert("Ett fel uppstod vid radering.");
      }
    };

    // Save polygon
    window.adminMapSave = async () => {
      if (!selectedAreaId) return alert("Vänligen välj ett område först!");
      if (!polygonLayer) return alert("Inga koordinater att spara! Rita en polygon först.");

      const latlngs = polygonLayer.getLatLngs()[0].map((latlng, i) => ({
        lat: latlng.lat,
        lng: latlng.lng,
        order: i,
      }));

      // Use mock operations for local development
      if (IS_LOCAL_DEV) {
        saveMockPins(selectedAreaId, latlngs);
        alert("Koordinater sparade! (lokal mock)");
        if (onPolygonSaved) onPolygonSaved();
        return;
      }

      try {
        const token = await getToken();
        
        // First, try to delete existing pins (if any)
        await fetch(`${process.env.REACT_APP_API_URL}/api/pins/${selectedAreaId}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": token },
          credentials: "include",
        });

        // Then add the new pins
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pins/${selectedAreaId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": token },
          credentials: "include",
          body: JSON.stringify({ area: selectedAreaId, pins: latlngs }),
        });

        if (response.ok) {
          alert("Koordinater sparade!");
          if (onPolygonSaved) onPolygonSaved(); // Refresh data
        } else {
          alert("Det gick inte att spara koordinaterna.");
          console.error(response.statusText);
        }
      } catch (err) {
        console.error(err);
        alert("Ett fel uppstod vid sparning.");
      }
    };
  }, [polygonLayer, selectedAreaId, onPolygonSaved]);

  // Filter out the selected area from background polygons (it's shown via FeatureGroup for editing)
  const backgroundAreas = allAreas.filter(area => 
    area.name !== selectedArea && area.pins && area.pins.length >= 3
  );

  // --- Render the map ---
  return (
    <MapContainer
      center={[55.59, 13.02]}
      zoom={13}
      style={{ width: "100%", height: "100%", zIndex: 0 }}
    >
      {/* Google Maps tiles via GoogleMutant */}
      <GoogleMutantLayer />

      {/* Fly to selected area when it changes */}
      <FlyToArea selectedArea={selectedArea} allAreas={allAreas} />

      {/* Render all OTHER area polygons (not the selected one) */}
      {backgroundAreas.map(area => (
        <AreaPolygon
          key={area.id}
          area={area}
          isSelected={false}
          onAreaClick={handleAreaPolygonClick}
        />
      ))}

      {/* FeatureGroup for the selected area's editable polygon */}
      <FeatureGroup ref={featureGroupRef}>
        {/* Existing polygon data will be loaded via useEffect */}
      </FeatureGroup>

      <DrawControl 
        featureGroupRef={featureGroupRef} 
        onPolygonChange={handlePolygonChange} 
      />
    </MapContainer>
  );
};

export default AdminMap;